import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Cta } from "../Cta";
import { motion, useMotionValue } from "framer-motion";
import { useEffect, useRef } from "react";
import { PARTNERS } from "@/resources/constants";
import Image from "next/image";
import { useTranslation } from "next-i18next";

const Hero = () => {
  const { t } = useTranslation('home');
  // Partner carousel setup
  const itemWidth = 140;
  const allPartners = [...PARTNERS, ...PARTNERS]; // Duplicate for seamless loop
  const totalItemsCount = allPartners.length;
  const speed = 1;
  const carouselRef = useRef(null);
  const x = useMotionValue(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      let newX = x.get() - speed;
      if (newX < -totalItemsCount * itemWidth / 2) {
        newX = 0;
      }
      x.set(newX);
    }, 20);
    return () => clearInterval(intervalId);
  }, [x, totalItemsCount]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary">
        <div className="absolute inset-0 opacity-20">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 pt-20 md:pt-24">
        <div className="max-w-5xl mx-auto text-center">
          {/* Text Content */}
          <div className="space-y-8 animate-fade-up pb-32">
            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
              {t('hero.title')}
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-secondary-foreground leading-relaxed max-w-4xl mx-auto">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
              <Cta size="large" className="text-base sm:text-lg px-6 py-4 sm:px-8 sm:py-6">
                {t('hero.cta')}
              </Cta>
              {/* <Cta size="large" primary={false} className="text-base sm:text-lg px-6 py-4 sm:px-8 sm:py-6">
                Request Demo
              </Cta> */}
            </div>

            {/* Trust Statement */}
            <p className="text-muted-foreground text-lg pt-4">
              {t('hero.trust')}
            </p>
          </div>
        </div>
      </div>

      {/* Partner Logos Carousel */}
      <div className="absolute bottom-8 w-full flex items-center justify-center">
        <div className="flex overflow-hidden mx-auto max-w-6xl">
          <motion.div
            className="flex gap-4"
            ref={carouselRef}
            style={{ x }}
          >
            {allPartners.map((partner, index) => {
              return (
                <div
                  key={index}
                  className="rounded-lg flex items-center justify-center min-w-[120px] md:min-w-[140px]"
                >
                  <Image
                    height={64}
                    width={140}
                    className="h-12 md:h-16 w-auto object-contain opacity-50 grayscale"
                    src={partner}
                    alt="Partner logo"
                  />
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
